import React, { useEffect } from 'react';
import { TourStep } from './TourStep';
import { useTourContext } from './TourProvider';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle } from 'lucide-react';

export function TourOverlay() {
  const {
    tourState,
    isActiveTour,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    closeTour,
    getCurrentStepIndex,
    getTotalSteps,
    getStepTranslation,
  } = useTourContext();

  const { t } = useTranslation();

  // Skip confirmation dialog - MUST be called before any early returns
  const [showSkipConfirm, setShowSkipConfirm] = React.useState(false);

  // Calculate values - safe to do even if tour is not active
  const currentStepIndex = getCurrentStepIndex();
  const totalSteps = getTotalSteps();
  const isLastStep = currentStepIndex === totalSteps - 1;
  const canGoBack = currentStepIndex > 0;
  const canGoForward = currentStepIndex < totalSteps - 1;

  // Get current step translation - safe to call even if no tour
  const stepTranslation = tourState ? getStepTranslation(tourState.currentStep) : null;

  // Handle key events - HOOK MUST BE CALLED BEFORE EARLY RETURNS
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActiveTour) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          closeTour();
          break;
        case 'ArrowRight':
          if (canGoForward) {
            e.preventDefault();
            nextStep();
          }
          break;
        case 'ArrowLeft':
          if (canGoBack) {
            e.preventDefault();
            previousStep();
          }
          break;
        case 'Enter':
          if (isLastStep) {
            e.preventDefault();
            completeTour();
          } else if (canGoForward) {
            e.preventDefault();
            nextStep();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isActiveTour,
    canGoForward,
    canGoBack,
    isLastStep,
    nextStep,
    previousStep,
    completeTour,
    closeTour,
  ]);

  // Handle skip confirmation
  const handleSkipConfirm = () => {
    setShowSkipConfirm(false);
    skipTour();
  };

  // Handle skip request
  const handleSkipRequest = () => {
    setShowSkipConfirm(true);
  };

  // Don't render if no active tour - AFTER all hooks are called
  if (!isActiveTour || !tourState) return null;

  // Show completion message
  if (tourState.isCompleted) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="mx-4 max-w-md rounded-lg bg-background p-8 text-center"
          >
            <div className="mb-4 flex justify-center">
              <CheckCircle className="size-12 text-green-500" />
            </div>
            <h2 className="mb-2 text-2xl font-bold">
              {stepTranslation?.title || t('common.completed', 'Tour Completed!')}
            </h2>
            <p className="mb-6 text-muted-foreground">
              {stepTranslation?.description ||
                t('common.completedDescription', "You're now ready to use KernLogic!")}
            </p>
            <Button onClick={closeTour} className="w-full">
              {t('common.buttons.close', 'Close')}
            </Button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <>
      {/* Tour Step */}
      <TourStep
        step={tourState.currentStep}
        title={stepTranslation?.title || 'Tour Step'}
        description={stepTranslation?.description || 'Description'}
        actionText={stepTranslation?.actionText || 'Next'}
        emphasize={stepTranslation?.emphasize || false}
        currentStep={currentStepIndex}
        totalSteps={totalSteps}
        onNext={isLastStep ? completeTour : nextStep}
        onPrevious={previousStep}
        onSkip={handleSkipRequest}
        onClose={closeTour}
        canGoBack={canGoBack}
        canGoForward={canGoForward || isLastStep}
        isLastStep={isLastStep}
        isActive={true}
      />

      {/* Skip Confirmation Dialog */}
      <Dialog open={showSkipConfirm} onOpenChange={setShowSkipConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="size-5 text-amber-500" />
              {t('common.navigation.skipConfirm', 'Are you sure you want to skip the tour?')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'common.navigation.skipConfirmDescription',
                'You can always restart the tour from your settings.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSkipConfirm(false)}>
              {t('common.navigation.continueWithTour', 'Continue with tour')}
            </Button>
            <Button variant="destructive" onClick={handleSkipConfirm}>
              {t('common.navigation.confirmSkip', 'Yes, skip tour')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
